import React, { useEffect, useState } from 'react';
import {
  Button,
  Label,
  Modal,
  ModalContent,
  ModalTitle,
  FormText,
  MultiSelect,
  MultiSelectItem,
  Select,
  SelectItem,
  SearchBox,
} from '@energybox/react-ui-library/dist/components';
import { isDefined } from '@energybox/react-ui-library/dist/utils';
import ModalFormContent from '../../components/ModalFormContent';
import {
  CircuitBreakerInPanel,
  DistributionPanel,
} from '@energybox/react-ui-library/dist/types';
import { pathOr } from 'ramda';
import { connect } from 'react-redux';
import { getDistributionPanelsBySiteId } from '../../actions/distribution_panel';
import { getEnergyProsByDistributionPanelId } from '../../actions/energy_pros';
import { ApplicationState } from '../../reducers';
import {
  EnergyProIdsByDistributionPanelId,
  EnergyProsById,
} from '../../reducers/energy_pros';
import multiSelectStyles from '../Selects/MultiSelects.module.css';
import { patchDerivedEnergyDP } from '../../actions/equipment';
import {
  DerivedEnergyBreakerData,
  DerivedEnergyFields,
  DerivedEnergyStateByEquipmentId,
} from '../../reducers/equipment';
import {
  sortBreakersByTitle,
  sortMainBreakersByPort,
} from './DerivedEnergyConsumptionTable';
import { useConfirmationModal } from '../../hooks/useConfirmationModal';
import styles from './EditDerivedEnergyConsumptionModal.module.css';

interface OwnProps {
  isNew: boolean;
  isVisible: boolean;
  siteId: string;
  equipmentId: string;
  editingDPId: string | null;
}
interface Props extends OwnProps {
  distributionPanels: DistributionPanel[];
  derivedEnergy: DerivedEnergyStateByEquipmentId;
  loadDistributionPanels: (siteId: string) => void;
  getEnergyProsByDistributionPanelId: (distributionPanelId: string) => void;
  energyProsById: EnergyProsById;
  energyProIdsByDistributionPanelId: EnergyProIdsByDistributionPanelId;
  onClose: () => void;
  onSubmit: (
    data: DerivedEnergyFields & { distributionPanelId: string }
  ) => void;
}

const breakerToBreakerOption = (
  breaker: CircuitBreakerInPanel
): DerivedEnergyBreakerData => ({
  id: breaker?.breaker?.id?.toString() || '',
  title: breaker?.breaker?.title || '',
});

const EditDerivedEnergyConsumptionModal: React.FC<Props> = ({
  siteId,
  isNew,
  isVisible,
  equipmentId,
  editingDPId,
  loadDistributionPanels,
  getEnergyProsByDistributionPanelId,
  energyProsById,
  energyProIdsByDistributionPanelId,
  onClose,
  onSubmit,
  distributionPanels,
  derivedEnergy,
}) => {
  const filteredDistributionPanels = distributionPanels.filter(
    panel =>
      !derivedEnergy.derivedEnergyByDistributionPanelId?.[
        panel?.id?.toString()
      ] || false
  );

  const [distributionPanelId, setDistributionPanelId] = useState<
    string | undefined
  >(undefined);
  const [mainBreakersToAdd, setMainBreakersToAdd] = useState<
    DerivedEnergyBreakerData[]
  >([]);
  const [mainBreakersToRemove, setMainBreakersToRemove] = useState<
    DerivedEnergyBreakerData[]
  >([]);
  const [breakersToAdd, setBreakersToAdd] = useState<
    DerivedEnergyBreakerData[]
  >([]);
  const [breakersToRemove, setBreakersToRemove] = useState<
    DerivedEnergyBreakerData[]
  >([]);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [mainBreakersSearchQuery, setMainBreakersSearchQuery] = useState('');
  const [breakersToAddSearchQuery, setBreakersToAddSearchQuery] = useState('');
  const [
    breakersToRemoveSearchQuery,
    setBreakersToRemoveSearchQuery,
  ] = useState('');
  const { openModal, ConfirmationModal } = useConfirmationModal();

  useEffect(() => {
    loadDistributionPanels(siteId);
  }, [siteId]);

  useEffect(() => {
    if (isVisible && !isNew && isDefined(editingDPId)) {
      const derivedEnergyFields =
        derivedEnergy.derivedEnergyByDistributionPanelId[
          editingDPId.toString()
        ];
      const {
        mainBreakerSensorsToAdd,
        mainBreakerSensorsToRemove,
        breakersToAdd,
        breakersToRemove,
      } = derivedEnergyFields;
      setDistributionPanelId(editingDPId?.toString() || '');
      setMainBreakersToAdd(mainBreakerSensorsToAdd);
      setMainBreakersToRemove(mainBreakerSensorsToRemove);
      setBreakersToAdd(breakersToAdd);
      setBreakersToRemove(breakersToRemove);
    }
  }, [isVisible, isNew, editingDPId]);

  useEffect(() => {
    if (isDefined(distributionPanelId)) {
      getEnergyProsByDistributionPanelId(distributionPanelId.toString());
    }
  }, [distributionPanelId]);

  const handleDistributionPanelSelect = (panelId: string) => {
    setDistributionPanelId(panelId.toString());
    if (formErrors.distributionPanelId) {
      setFormErrors(prev => ({ ...prev, distributionPanelId: '' }));
    }
    // Clear previous selections
    setMainBreakersToAdd([]);
    setMainBreakersToRemove([]);
    setBreakersToAdd([]);
    setBreakersToRemove([]);
  };

  const handleAddMainBreaker = (breaker: DerivedEnergyBreakerData) => {
    if (!mainBreakersToAdd.find(b => b.id === breaker.id)) {
      setMainBreakersToAdd(prev => [...prev, breaker]);
    }
  };

  const handleAddMainBreakerToRemove = (breaker: DerivedEnergyBreakerData) => {
    if (!mainBreakersToRemove.find(b => b.id === breaker.id)) {
      setMainBreakersToRemove(prev => [...prev, breaker]);
    }
  };

  const handleRemoveMainBreaker = (breakerId: string) => {
    setMainBreakersToAdd(prev => prev.filter(b => b.id !== breakerId));
  };

  const handleRemoveMainBreakerToRemove = (breakerId: string) => {
    setMainBreakersToRemove(prev => prev.filter(b => b.id !== breakerId));
  };

  const handleAddBreaker = (breaker: DerivedEnergyBreakerData) => {
    if (!breakersToAdd.find(b => b.id === breaker.id)) {
      setBreakersToAdd(prev => [...prev, breaker]);
    }
  };

  const handleRemoveBreaker = (breakerId: string) => {
    setBreakersToAdd(prev => prev.filter(b => b.id !== breakerId));
  };

  const handleAddBreakerToRemove = (breaker: DerivedEnergyBreakerData) => {
    if (!breakersToRemove.find(b => b.id === breaker.id)) {
      setBreakersToRemove(prev => [...prev, breaker]);
    }
  };

  const handleRemoveBreakerToRemove = (breakerId: string) => {
    setBreakersToRemove(prev => prev.filter(b => b.id !== breakerId));
  };

  // Get available breakers for the selected distribution panel
  const getAvailableBreakers = () => {
    if (!distributionPanelId) return [];
    const selectedDistributionPanel = distributionPanels.find(
      p => p.id.toString() === distributionPanelId
    );
    return (
      selectedDistributionPanel?.breakers
        ?.map(breaker => breakerToBreakerOption(breaker))
        .sort((a, b) => sortBreakersByTitle(a.title, b.title)) || []
    );
  };

  const getAvailableBreakersToAdd = () => {
    return getAvailableBreakers().filter(
      breaker => !breakersToRemove.some(b => b.id === breaker.id)
    );
  };

  const getAvailableBreakersToRemove = () => {
    return getAvailableBreakers().filter(
      breaker => !breakersToAdd.some(b => b.id === breaker.id)
    );
  };

  // Get main breaker sensors from energy pro
  const getMainBreakerSensors = (): DerivedEnergyBreakerData[] => {
    if (
      !distributionPanelId ||
      !isDefined(
        energyProIdsByDistributionPanelId[distributionPanelId.toString()]
      )
    ) {
      return [];
    }
    const energyPro = energyProIdsByDistributionPanelId[
      distributionPanelId.toString()
    ]
      ? energyProsById[
          energyProIdsByDistributionPanelId[distributionPanelId.toString()][0]
        ]
      : null;
    return energyPro
      ? energyPro.sensors
          .sort((a, b) => a.port - b.port)
          .map(sensor => ({
            id: sensor.id?.toString() || '',
            title:
              (sensor.breaker?.title &&
                `${sensor.breaker.title} pole ${sensor.breakerPole}`) ||
              '',
          }))
      : [];
  };

  const getAvailableMainBreakersToAdd = () => {
    return getMainBreakerSensors().filter(
      sensor => !mainBreakersToRemove.some(b => b.id === sensor.id)
    );
  };

  const getAvailableMainBreakersToRemove = () => {
    return getMainBreakerSensors().filter(
      sensor => !mainBreakersToAdd.some(b => b.id === sensor.id)
    );
  };

  // Toggle Select All functions
  const toggleSelectAllMainBreakers = () => {
    const availableMainBreakers = getAvailableMainBreakersToAdd();
    if (!availableMainBreakers.length) return;
    if (
      mainBreakersToAdd.length === availableMainBreakers.length ||
      mainBreakersToAdd.length > 0
    ) {
      setMainBreakersToAdd([]);
    } else {
      setMainBreakersToAdd(availableMainBreakers);
    }
  };

  const toggleSelectAllMainBreakersToRemove = () => {
    const availableMainBreakers = getAvailableMainBreakersToRemove();
    if (!availableMainBreakers.length) return;
    if (
      mainBreakersToRemove.length === availableMainBreakers.length ||
      mainBreakersToRemove.length > 0
    ) {
      setMainBreakersToRemove([]);
    } else {
      setMainBreakersToRemove(availableMainBreakers);
    }
  };

  const toggleSelectAllBreakersToAdd = () => {
    const availableBreakers = getAvailableBreakersToAdd();
    if (!availableBreakers.length) return;
    if (
      breakersToAdd.length === availableBreakers.length ||
      breakersToAdd.length > 0
    ) {
      setBreakersToAdd([]);
    } else {
      setBreakersToAdd(availableBreakers);
    }
  };

  const toggleSelectAllBreakersToRemove = () => {
    const availableBreakers = getAvailableBreakersToRemove();
    if (!availableBreakers.length) return;
    if (
      breakersToRemove.length === availableBreakers.length ||
      breakersToRemove.length > 0
    ) {
      setBreakersToRemove([]);
    } else {
      setBreakersToRemove(availableBreakers);
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!distributionPanelId) {
      errors.distributionPanelId = 'Distribution Panel is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const panelTitle =
        distributionPanels.find(p => p.id.toString() === distributionPanelId)
          ?.title || '';

      openModal(
        {
          title: isNew
            ? 'Attach Distribution Panel'
            : 'Edit Distribution Panel',
          description: isNew
            ? `Are you sure you want to attach "${panelTitle}" to this equipment's derived energy configuration?`
            : `Are you sure you want to save changes to "${panelTitle}"?`,
          confirmText: isNew ? 'Add' : 'Save',
          cancelText: 'Cancel',
        },
        () => {
          onSubmit({
            distributionPanelId: distributionPanelId?.toString() || '',
            distributionPanelTitle: panelTitle,
            mainBreakerSensorsToAdd: mainBreakersToAdd,
            mainBreakerSensorsToRemove: mainBreakersToRemove,
            breakersToAdd,
            breakersToRemove,
          });
          handleClose();
        }
      );
    }
  };

  const handleClose = () => {
    setDistributionPanelId(undefined);
    setMainBreakersToAdd([]);
    setMainBreakersToRemove([]);
    setBreakersToAdd([]);
    setBreakersToRemove([]);
    setFormErrors({});
    setMainBreakersSearchQuery('');
    setBreakersToAddSearchQuery('');
    setBreakersToRemoveSearchQuery('');
    onClose();
  };

  const actions = (
    <>
      <Button variant='text' onClick={handleClose}>
        Cancel
      </Button>
      <Button onClick={handleSubmit}>{isNew ? 'Add' : 'Save'}</Button>
    </>
  );

  if (!isVisible) return null;

  return (
    <>
      <Modal onClose={handleClose} actions={actions} disableEscapeClose>
        <ModalTitle>
          {isNew ? 'Attach Distribution Panel' : 'Edit Distribution Panel'}
        </ModalTitle>
        <ModalContent>
          <ModalFormContent className={styles.modalFormContent}>
            <div>
              <Label required>Distribution Panel</Label>
            </div>
            <div>
              <Select
                variant='select'
                title={
                  distributionPanelId
                    ? distributionPanels.find(
                        p => p.id.toString() === distributionPanelId
                      )?.title || 'Select Distribution Panel'
                    : 'Select Distribution Panel'
                }
                value={distributionPanelId}
                onClick={() => {}}
                disabled={!isNew && isDefined(editingDPId)}
              >
                {filteredDistributionPanels.map(panel => (
                  <SelectItem
                    key={panel.id}
                    onSelect={() =>
                      handleDistributionPanelSelect(panel.id.toString())
                    }
                  >
                    {panel.title}
                  </SelectItem>
                ))}
              </Select>
              {formErrors.distributionPanelId && (
                <div
                  style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}
                >
                  {formErrors.distributionPanelId}
                </div>
              )}
            </div>
          </ModalFormContent>

          <ModalFormContent className={styles.modalFormContent}>
            <div>
              <Label>Main Breakers to Add</Label>
            </div>
            <div>
              <MultiSelect
                doNotIncludeActions
                selectionComponentType='portalDropdown'
                onDeleteItem={(e, itemId) =>
                  handleRemoveMainBreaker(String(itemId))
                }
                items={mainBreakersToAdd.map(breaker => ({
                  value: breaker.title,
                  className: '',
                  id: breaker.id,
                }))}
                selectionComponent={
                  <div className={multiSelectStyles.dropdown}>
                    <div className={multiSelectStyles.searchBox}>
                      <SearchBox
                        error={
                          getAvailableMainBreakersToAdd().filter(sensor =>
                            sensor.title
                              .toLowerCase()
                              .includes(mainBreakersSearchQuery.toLowerCase())
                          ).length === 0
                        }
                        withDropdown={false}
                        query={mainBreakersSearchQuery}
                        width={'100%'}
                        widthActive={'100%'}
                        placeholder={'Search Main Breakers'}
                        onChange={setMainBreakersSearchQuery}
                        noClickOutsideDetection
                      />
                    </div>
                    <div className={multiSelectStyles.dropdownList}>
                      <div className={multiSelectStyles.selectAll}>
                        <span
                          tabIndex={0}
                          onKeyPress={toggleSelectAllMainBreakers}
                          role='button'
                          onClick={toggleSelectAllMainBreakers}
                        >
                          {mainBreakersToAdd.length > 0
                            ? 'Deselect All'
                            : 'Select All'}
                        </span>
                      </div>
                      {getAvailableMainBreakersToAdd()
                        .filter(sensor =>
                          sensor.title
                            .toLowerCase()
                            .includes(mainBreakersSearchQuery.toLowerCase())
                        )
                        .map((sensor, index) => {
                          const isSelected = mainBreakersToAdd.some(
                            b => b.id === sensor.id
                          );
                          return (
                            <MultiSelectItem
                              key={`mainBreakerToAdd-${sensor.id || index}`}
                              isSelected={isSelected}
                              onSelect={() =>
                                isSelected
                                  ? handleRemoveMainBreaker(sensor.id)
                                  : handleAddMainBreaker(sensor)
                              }
                            >
                              {sensor.title}
                            </MultiSelectItem>
                          );
                        })}
                    </div>
                  </div>
                }
                onSelection={() => {}}
                error={false}
                customErrorText={''}
              />
            </div>
          </ModalFormContent>

          <ModalFormContent className={styles.modalFormContent}>
            <div>
              <Label>Main Breakers to Remove</Label>
            </div>
            <div>
              <MultiSelect
                doNotIncludeActions
                selectionComponentType='portalDropdown'
                onDeleteItem={(e, itemId) =>
                  handleRemoveMainBreakerToRemove(String(itemId))
                }
                items={mainBreakersToRemove.map(breaker => ({
                  value: breaker.title,
                  className: '',
                  id: breaker.id,
                }))}
                selectionComponent={
                  <div className={multiSelectStyles.dropdown}>
                    <div className={multiSelectStyles.searchBox}>
                      <SearchBox
                        error={
                          getAvailableMainBreakersToRemove().filter(sensor =>
                            sensor.title
                              .toLowerCase()
                              .includes(mainBreakersSearchQuery.toLowerCase())
                          ).length === 0
                        }
                        withDropdown={false}
                        query={mainBreakersSearchQuery}
                        width={'100%'}
                        widthActive={'100%'}
                        placeholder={'Search Main Breakers'}
                        onChange={setMainBreakersSearchQuery}
                        noClickOutsideDetection
                      />
                    </div>
                    <div className={multiSelectStyles.dropdownList}>
                      <div className={multiSelectStyles.selectAll}>
                        <span
                          tabIndex={0}
                          onKeyPress={toggleSelectAllMainBreakersToRemove}
                          role='button'
                          onClick={toggleSelectAllMainBreakersToRemove}
                        >
                          {mainBreakersToRemove.length > 0
                            ? 'Deselect All'
                            : 'Select All'}
                        </span>
                      </div>
                      {getAvailableMainBreakersToRemove()
                        .filter(sensor =>
                          sensor.title
                            .toLowerCase()
                            .includes(mainBreakersSearchQuery.toLowerCase())
                        )
                        .map((sensor, index) => {
                          const isSelected = mainBreakersToRemove.some(
                            b => b.id === sensor.id
                          );
                          return (
                            <MultiSelectItem
                              key={`mainBreakerToRemove-${sensor.id || index}`}
                              isSelected={isSelected}
                              onSelect={() =>
                                isSelected
                                  ? handleRemoveMainBreakerToRemove(sensor.id)
                                  : handleAddMainBreakerToRemove(sensor)
                              }
                            >
                              {sensor.title}
                            </MultiSelectItem>
                          );
                        })}
                    </div>
                  </div>
                }
                onSelection={() => {}}
                error={false}
                customErrorText={''}
              />
            </div>
          </ModalFormContent>

          <ModalFormContent className={styles.modalFormContent}>
            <div>
              <Label>Breakers to Add</Label>
            </div>
            <div>
              <MultiSelect
                doNotIncludeActions
                selectionComponentType='portalDropdown'
                onDeleteItem={(e, itemId) =>
                  handleRemoveBreaker(String(itemId))
                }
                items={breakersToAdd.map(breaker => ({
                  value: breaker.title,
                  className: '',
                  id: breaker.id,
                }))}
                selectionComponent={
                  <div className={multiSelectStyles.dropdown}>
                    <div className={multiSelectStyles.searchBox}>
                      <SearchBox
                        error={
                          getAvailableBreakersToAdd().filter(breaker =>
                            breaker.title
                              .toLowerCase()
                              .includes(breakersToAddSearchQuery.toLowerCase())
                          ).length === 0
                        }
                        withDropdown={false}
                        query={breakersToAddSearchQuery}
                        width={'100%'}
                        widthActive={'100%'}
                        placeholder={'Search Breakers'}
                        onChange={setBreakersToAddSearchQuery}
                        noClickOutsideDetection
                      />
                    </div>
                    <div className={multiSelectStyles.dropdownList}>
                      <div className={multiSelectStyles.selectAll}>
                        <span
                          tabIndex={0}
                          onKeyPress={toggleSelectAllBreakersToAdd}
                          role='button'
                          onClick={toggleSelectAllBreakersToAdd}
                        >
                          {breakersToAdd.length > 0
                            ? 'Deselect All'
                            : 'Select All'}
                        </span>
                      </div>
                      {getAvailableBreakersToAdd()
                        .filter(breaker =>
                          breaker.title
                            .toLowerCase()
                            .includes(breakersToAddSearchQuery.toLowerCase())
                        )
                        .map((breaker, index) => {
                          const isSelected = breakersToAdd.some(
                            b => b.id === breaker.id
                          );
                          return (
                            <MultiSelectItem
                              key={`breakerToAdd-${breaker.id || index}`}
                              isSelected={isSelected}
                              onSelect={() =>
                                isSelected
                                  ? handleRemoveBreaker(breaker.id)
                                  : handleAddBreaker(breaker)
                              }
                            >
                              {breaker.title}
                            </MultiSelectItem>
                          );
                        })}
                    </div>
                  </div>
                }
                onSelection={() => {}}
                error={false}
                customErrorText={''}
              />
            </div>
          </ModalFormContent>

          <ModalFormContent className={styles.modalFormContent}>
            <div>
              <Label>Breakers to Remove</Label>
            </div>
            <div>
              <MultiSelect
                doNotIncludeActions
                selectionComponentType='portalDropdown'
                onDeleteItem={(e, itemId) =>
                  handleRemoveBreakerToRemove(String(itemId))
                }
                items={breakersToRemove.map(breaker => ({
                  value: breaker.title,
                  className: '',
                  id: breaker.id,
                }))}
                selectionComponent={
                  <div className={multiSelectStyles.dropdown}>
                    <div className={multiSelectStyles.searchBox}>
                      <SearchBox
                        error={
                          getAvailableBreakersToRemove().filter(breaker =>
                            breaker.title
                              .toLowerCase()
                              .includes(
                                breakersToRemoveSearchQuery.toLowerCase()
                              )
                          ).length === 0
                        }
                        withDropdown={false}
                        query={breakersToRemoveSearchQuery}
                        width={'100%'}
                        widthActive={'100%'}
                        placeholder={'Search Breakers'}
                        onChange={setBreakersToRemoveSearchQuery}
                        noClickOutsideDetection
                      />
                    </div>
                    <div className={multiSelectStyles.dropdownList}>
                      <div className={multiSelectStyles.selectAll}>
                        <span
                          tabIndex={0}
                          onKeyPress={toggleSelectAllBreakersToRemove}
                          role='button'
                          onClick={toggleSelectAllBreakersToRemove}
                        >
                          {breakersToRemove.length > 0
                            ? 'Deselect All'
                            : 'Select All'}
                        </span>
                      </div>
                      {getAvailableBreakersToRemove()
                        .filter(breaker =>
                          breaker.title
                            .toLowerCase()
                            .includes(breakersToRemoveSearchQuery.toLowerCase())
                        )
                        .map((breaker, index) => {
                          const isSelected = breakersToRemove.some(
                            b => b.id === breaker.id
                          );
                          return (
                            <MultiSelectItem
                              key={`breakerToRemove-${breaker.id || index}`}
                              isSelected={isSelected}
                              onSelect={() =>
                                isSelected
                                  ? handleRemoveBreakerToRemove(breaker.id)
                                  : handleAddBreakerToRemove(breaker)
                              }
                            >
                              {breaker.title}
                            </MultiSelectItem>
                          );
                        })}
                    </div>
                  </div>
                }
                onSelection={() => {}}
                error={false}
                customErrorText={''}
              />
            </div>
          </ModalFormContent>

          <ModalFormContent className={styles.modalFormContent}>
            <FormText>* Mandatory fields</FormText>
          </ModalFormContent>
        </ModalContent>
      </Modal>
      <ConfirmationModal />
    </>
  );
};

const mapStateToProps = (
  { distributionPanels, energyPros, equipment }: ApplicationState,
  { siteId, equipmentId }: OwnProps
) => ({
  distributionPanels: pathOr(
    [],
    [],
    distributionPanels.distributionPanelsBySiteId[siteId]
  ),
  energyProsById: energyPros.energyProsById,
  energyProIdsByDistributionPanelId:
    energyPros.energyProIdsByDistributionPanelId,
  derivedEnergy: equipment.derivedEnergyByEquipmentId[equipmentId] || {},
});

const mapDispatchToProps = (dispatch: any, { equipmentId }: OwnProps) => ({
  loadDistributionPanels: (siteId: string | number) =>
    dispatch(getDistributionPanelsBySiteId(siteId)),
  getEnergyProsByDistributionPanelId: (distributionPanelId: string | number) =>
    dispatch(getEnergyProsByDistributionPanelId(distributionPanelId)),
  onSubmit: (formData: DerivedEnergyFields & { distributionPanelId: string }) =>
    dispatch(
      patchDerivedEnergyDP(equipmentId, formData.distributionPanelId, {
        distributionPanelTitle: formData.distributionPanelTitle,
        mainBreakerSensorsToAdd: formData.mainBreakerSensorsToAdd,
        mainBreakerSensorsToRemove: formData.mainBreakerSensorsToRemove,
        breakersToAdd: formData.breakersToAdd,
        breakersToRemove: formData.breakersToRemove,
      })
    ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EditDerivedEnergyConsumptionModal);
